/**
 * CustomerScorecardCard — controls for generating and downloading the
 * one-page Customer Safety Scorecard PDF. Sits on the Reports page.
 *
 * Auth: PDF endpoint reads the httpOnly f2_token cookie automatically
 * on same-origin navigation, so we just set `window.location.href`
 * with the correct customer + month query params. No sign step needed.
 */
import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

// Default the month picker to the previous full calendar month, since
// the scorecard is typically pulled AFTER a month closes. The user
// can still bump forward or back via the input.
function defaultMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
}

export default function CustomerScorecardCard() {
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [month, setMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reports/customers")
      .then((r) => setCustomers(r.data.customers || []))
      .catch(() => setCustomers([]));
  }, []);

  const download = () => {
    if (!customer) {
      toast.error("Pick a customer first.");
      return;
    }
    if (!month) {
      toast.error("Pick a month first.");
      return;
    }
    setLoading(true);
    // Same-origin navigation — the browser attaches the httpOnly
    // f2_token cookie automatically, so no Authorization header is
    // needed. The endpoint returns Content-Disposition: attachment
    // so the browser fires the download instead of navigating.
    const url = `${API_BASE}/reports/customer-scorecard.pdf?customer=${encodeURIComponent(customer)}&month=${encodeURIComponent(month)}`;
    window.location.href = url;
    // Small delay before releasing the button so double-clicks don't
    // fire two nav requests.
    setTimeout(() => setLoading(false), 1500);
  };

  return (
    <Card
      className="bg-slate-900 border-slate-800 mt-4"
      data-testid="customer-scorecard-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-md border border-emerald-800 bg-emerald-950/40 text-emerald-400 shrink-0">
            <ClipboardCheck className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">
              Customer Safety Scorecard
            </div>
            <div className="font-display font-bold text-lg tracking-tight">
              Monthly compliance one-pager
            </div>
            <div className="text-xs text-slate-400 mt-0.5 max-w-2xl">
              Generates a branded PDF for the selected customer + month:
              overall compliance %, audit count, stop-works, compliance trend chart,
              top 3 sites by non-compliance, and every stop-work event.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1">
              Customer
            </label>
            <select
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              data-testid="scorecard-customer-select"
            >
              <option value="">— Choose a customer —</option>
              {customers.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full h-9 bg-slate-950 border border-slate-700 rounded-md px-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
              data-testid="scorecard-month-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={download}
              disabled={!customer || !month || loading}
              className="w-full bg-red-700 hover:bg-red-600 font-display tracking-wider disabled:opacity-50"
              data-testid="scorecard-download-btn"
            >
              <FileText className="w-4 h-4 mr-1.5" />
              {loading ? "GENERATING…" : "DOWNLOAD PDF"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
